/* eslint-disable max-len */
import { Col, Row } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type GiftVoucherModuleSpecsFragment } from '../../../../api/fragments';
import DealershipTranslatedTextAreaField from '../../../../components/fields/DealershipFields/DealershipTranslatedTextArea';
import SingleUploadField from '../../../../components/fields/SingleUploadField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import { allowedExtensions } from '../../../../utilities/extensions';

type GiftVoucherModuleEmailFormProps = {
    prefix?: string;
    module: GiftVoucherModuleSpecsFragment;
};

type GiftVoucherModuleItemProps = {
    path: 'customer.confirmation' | 'salesPerson.confirmation';
    prefix?: string;
    module: GiftVoucherModuleSpecsFragment;
    children?: React.ReactNode;
};

const GiftVoucherModuleEmailItem = ({ path, prefix, module, children }: GiftVoucherModuleItemProps) => {
    const { t } = useTranslation('giftVoucherModuleDetails');

    const getName = useCallback(
        (name: string) => (prefix ? `${prefix}.${path}.${name}` : `${path}.${name}`),
        [path, prefix]
    );
    const getTranslationKey = useCallback((name: string) => `giftVoucherModuleDetails:fields.emails.${name}`, []);

    return (
        <>
            <Col lg={8} xs={24}>
                <DealershipTranslatedTextAreaField
                    {...t(getTranslationKey('subject'), { returnObjects: true })}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    companyId={module.company.id}
                    name={getName('subject')}
                    required
                />
            </Col>
            <Col lg={8} xs={24}>
                <DealershipTranslatedTextAreaField
                    {...t(getTranslationKey('introTitle'), { returnObjects: true })}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    companyId={module.company.id}
                    name={getName('introTitle')}
                    required
                />
            </Col>
            <Col lg={8} xs={24}>
                <DealershipTranslatedTextAreaField
                    {...t(getTranslationKey('contentText'), { returnObjects: true })}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    companyId={module.company.id}
                    name={getName('contentText')}
                    required
                    withContentRefinement
                />
            </Col>
            {children}
            <Col lg={12} xs={24}>
                <SingleUploadField
                    extensions={allowedExtensions.image}
                    {...t(getTranslationKey('introImage'), { returnObjects: true })}
                    name={getName('introImage')}
                />
            </Col>
        </>
    );
};

const GiftVoucherModuleEmailForm = ({ prefix, module }: GiftVoucherModuleEmailFormProps) => {
    const { t } = useTranslation('giftVoucherModuleDetails');

    return (
        <CollapsibleWrapper
            defaultActiveKey={['emailContentsCustomerConfirmation', 'emailContentsSalespersonConfirmation']}
        >
            <Panel
                key="emailContentsCustomerConfirmation"
                header={t('giftVoucherModuleDetails:sections.emails.customer.confirmation')}
            >
                <Row gutter={10}>
                    <GiftVoucherModuleEmailItem module={module} path="customer.confirmation" prefix={prefix} />
                </Row>
            </Panel>
        </CollapsibleWrapper>
    );
};

export default GiftVoucherModuleEmailForm;
